
import { ScrollArea } from "@/components/ui/scroll-area";
import { A2AAgent } from "@/utils/a2a";
import { AnimatePresence, motion } from "framer-motion";
import { BrainCircuit, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { OrchestrationInsights } from "@/components/ai/types";

interface AgentOrchestratorStatusProps {
  activeAgents: string[];
  availableAgents: A2AAgent[];
  insights: OrchestrationInsights | null;
  className?: string;
}

export const AgentOrchestratorStatus = ({ 
  activeAgents, 
  availableAgents,
  insights,
  className 
}: AgentOrchestratorStatusProps) => {
  const getAgentById = (id: string) => {
    return availableAgents.find(agent => agent.id === id);
  };

  const renderInsightsContent = () => {
    if (!insights) {
      return (
        <div className="text-xs text-muted-foreground italic p-2">
          Agent communication will appear here...
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="text-xs border rounded-md p-2 bg-background"
        >
          <div className="font-medium mb-1">Task Analysis</div>
          <div>Complexity: {insights.taskComplexity}</div>
          <div>Confidence: {Math.round(insights.confidence * 100)}%</div>
          {insights.estimatedCompletion && (
            <div>ETA: {new Date(insights.estimatedCompletion).toLocaleTimeString()}</div>
          )}
        </motion.div>
        
        {insights.reasoning && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
            className="text-xs border rounded-md p-2 bg-background"
          >
            <div className="font-medium mb-1">Reasoning</div>
            <div>{insights.reasoning}</div>
          </motion.div>
        )}
      </div>
    );
  };

  return (
    <div className={cn("w-80 border-l bg-muted/20 flex flex-col h-full", className)}>
      <div className="p-3 border-b bg-muted/30">
        <h3 className="text-sm font-medium flex items-center gap-1.5">
          <BrainCircuit className="h-4 w-4 text-primary" />
          Agent Orchestration
        </h3>
      </div>

      <div className="p-3 border-b">
        <h4 className="text-xs font-medium text-muted-foreground mb-2">ACTIVE AGENTS</h4>
        <div className="space-y-2">
          <AnimatePresence>
            {activeAgents.map((agentId) => {
              const agent = getAgentById(agentId);
              if (!agent) return null;
              
              return (
                <motion.div
                  key={agent.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center gap-2 p-2 rounded-md bg-background border"
                >
                  <div className={cn(
                    "h-2 w-2 rounded-full animate-pulse",
                    agent.id === 'executive-agent' ? "bg-primary" : "bg-green-500"
                  )} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{agent.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{agent.description}</p>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="flex flex-wrap gap-1">
                          {agent.capabilities.slice(0, 2).map(capability => (
                            <Badge 
                              variant="secondary" 
                              key={capability}
                              className="text-xs py-0 h-5"
                            >
                              {capability.split('-')[0]}
                            </Badge>
                          ))}
                          {agent.capabilities.length > 2 && (
                            <Badge variant="outline" className="text-xs py-0 h-5">
                              +{agent.capabilities.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div className="text-xs space-y-1">
                          <p className="font-medium">Agent Capabilities:</p>
                          <ul className="list-disc pl-4 space-y-0.5">
                            {agent.capabilities.map(capability => (
                              <li key={capability}>{capability}</li>
                            ))}
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="p-3 border-b">
          <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <MessageSquare className="h-3 w-3" />
            ORCHESTRATION INSIGHTS
          </h4>
        </div>
        <ScrollArea className="flex-1 p-3">
          {renderInsightsContent()}
        </ScrollArea>
      </div>
    </div>
  );
};
